/**
 * "Svagaste ämnen" (slice D, P2-2): up to five tried topics with the lowest estimated
 * träffsäkerhet, each with an "Öva" link that preselects the topic on `/practice`. Shared by
 * Progress and the Tentaläge card on Home.
 */
import { Link } from "react-router";
import { Card } from "@client/components/ui";
import { practiceDeepLink } from "@client/features/practice/deepLink";
import { useLang, useT, useTp } from "@client/i18n";
import { formatPercent } from "@client/lib/format";
import type { TopicProgress } from "./types";
import { WEAKEST_MIN_ITEMS } from "./weakest";

export interface WeakestCardProps {
  topics: readonly TopicProgress[];
  topicLabel: (topicId: string) => string;
}

export function WeakestCard({ topics, topicLabel }: WeakestCardProps) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const title = t("progress.weakest.title");
  return (
    <Card title={title} compact>
      {topics.length === 0 ? (
        <p className="text-sm text-ink-600 dark:text-ink-400">
          {tp("progress.weakest.empty", WEAKEST_MIN_ITEMS)}
        </p>
      ) : (
        <ol aria-label={title} className="divide-y divide-ink-100 dark:divide-ink-900">
          {topics.map((topic) => (
            <li key={topic.topicId} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-800 dark:text-ink-100">
                  {topicLabel(topic.topicId)}
                </p>
                <p className="text-xs text-ink-500 dark:text-ink-400">
                  {t("progress.weakest.detail", {
                    percent: formatPercent(topic.mastery, lang),
                    items: topic.items,
                  })}
                </p>
              </div>
              <Link
                to={practiceDeepLink({ chapters: [topic.chapter], topics: [topic.topicId] })}
                aria-label={t("progress.weakest.ctaFor", { topic: topicLabel(topic.topicId) })}
                className="inline-flex h-8 items-center rounded-lg border border-ink-200 px-3 text-sm font-medium text-ink-800 hover:bg-ink-50 dark:border-ink-800 dark:text-ink-100 dark:hover:bg-ink-900"
              >
                {t("progress.weakest.cta")}
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
